"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b ||= {})
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
import classNames from "classnames";
import React, { useRef } from "react";
import Tooltip from "../Tooltip/Tooltip";
import { handleMouseUpByBlurring } from "../../../utils/focus";
import * as utils from "../../../utils";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";
import { useLayoutEffect } from "../../../utils/useIsomorphicLayoutEffect";
import { AnimationDuration } from "../../../styles/Animations";
import { Expandable } from "../../behaviors/Expandable";
import { IconSize, Warning } from "../../icons";
import { Text } from "../../pieces/Text";
export var SegmentedControlSize = /* @__PURE__ */ ((SegmentedControlSize2) => {
  SegmentedControlSize2["Medium"] = "medium";
  SegmentedControlSize2["Large"] = "large";
  return SegmentedControlSize2;
})(SegmentedControlSize || {});
export var SegmentedControlTheme = /* @__PURE__ */ ((SegmentedControlTheme2) => {
  SegmentedControlTheme2["Light"] = "light";
  SegmentedControlTheme2["Dark"] = "dark";
  return SegmentedControlTheme2;
})(SegmentedControlTheme || {});
const renderOption = (selectedIndex, buttonRef, size, option, selectedValue, onClickHandler, index, length, updateIndicatorPosition, tooltipSize) => {
  const handleClick = (event) => {
    event.stopPropagation();
    onClickHandler(option.value);
  };
  const isSelected = selectedValue === option.value;
  const isFirst = index === 0;
  const optionComponent = /* @__PURE__ */ React.createElement(
    "button",
    {
      "data-qwilr-component": "segment-option",
      type: "button",
      role: "radio",
      ref: buttonRef,
      "aria-label": option.icon && typeof option.label === "string" ? option.label : void 0,
      "aria-checked": isSelected,
      key: option.value.toString(),
      className: classNames("segmented-control__button", `segmented-control__button--${size}`, "center", {
        "segmented-control__button--selected": isSelected,
        "segmented-control__button--first": isFirst,
        "segmented-control__button--last": index === length - 1,
        "segmented-control__button--disabled": option.disabled
      }),
      onClick: option.disabled ? void 0 : handleClick,
      onMouseUp: handleMouseUpByBlurring
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classNames("segmented-control__button-divider", {
          "segmented-control__button-divider--show": !isFirst && !isSelected && index !== selectedIndex + 1
        })
      }
    ),
    /* @__PURE__ */ React.createElement(ReactResizeDetector, { handleWidth: true, onResize: updateIndicatorPosition }, /* @__PURE__ */ React.createElement("div", { className: "segmented-control__button-content center" }, renderOptionContent(option)))
  );
  if (option.tooltipMessage !== void 0) {
    return /* @__PURE__ */ React.createElement(Tooltip, { size: tooltipSize, key: option.value.toString(), content: option.tooltipMessage }, optionComponent);
  } else if (option.tooltipContainer !== void 0) {
    return option.tooltipContainer(optionComponent);
  } else {
    return optionComponent;
  }
};
const SegmentedControl = (props) => {
  const { options, selectedValue, onClickHandler, tooltipSize, className, error } = props;
  const size = props.size || "medium" /* Medium */;
  const theme = props.theme || "light" /* Light */;
  const [indicatorPosition, setIndicatorPosition] = React.useState();
  const optionButtonRefs = useRef([]);
  const baseId = useRef(`segmented-control-${utils.randomString()}`);
  const labelId = `${baseId.current}-label`;
  const errorId = `${baseId.current}-error`;
  const selectedIndex = options.findIndex((option) => option.value === selectedValue);
  const updateIndicatorPosition = () => {
    var _a, _b;
    const selectedButtonRef = optionButtonRefs.current[selectedIndex];
    const selectedButtonRect = (_a = selectedButtonRef == null ? void 0 : selectedButtonRef.current) == null ? void 0 : _a.getBoundingClientRect();
    setIndicatorPosition({
      left: (_b = selectedButtonRef == null ? void 0 : selectedButtonRef.current) == null ? void 0 : _b.offsetLeft,
      width: selectedButtonRect == null ? void 0 : selectedButtonRect.width
    });
  };
  useLayoutEffect(() => {
    updateIndicatorPosition();
  }, [selectedValue]);
  const optionComponents = options.map((option, index) => {
    const buttonRef = React.createRef();
    optionButtonRefs.current[index] = buttonRef;
    return renderOption(
      selectedIndex,
      buttonRef,
      size,
      option,
      selectedValue,
      onClickHandler,
      index,
      options.length,
      updateIndicatorPosition,
      tooltipSize
    );
  });
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      role: "radiogroup",
      "aria-labelledby": labelId,
      className: classNames("segmented-control", `segmented-control--${theme}`, className)
    },
    /* @__PURE__ */ React.createElement(
      "label",
      {
        id: labelId,
        className: classNames("segmented-control__label", {
          "segmented-control__label--hidden": props.labelHidden
        })
      },
      props.label
    ),
    /* @__PURE__ */ React.createElement("div", { className: classNames("segmented-control__content", `segmented-control__content--${size}`) }, !!(indicatorPosition == null ? void 0 : indicatorPosition.width) && /* @__PURE__ */ React.createElement(
      "div",
      {
        className: "segmented-control__indicator",
        style: {
          transform: `translate3d(${indicatorPosition.left}px, 0, 0)`,
          width: indicatorPosition.width
        }
      }
    ), optionComponents),
    /* @__PURE__ */ React.createElement(
      Expandable,
      {
        expanded: !!error,
        timeout: AnimationDuration.Short,
        "aria-live": "polite",
        "aria-labelled-by": labelId,
        id: errorId
      },
      ({ expandableElementRef }) => /* @__PURE__ */ React.createElement("div", { className: "segmented-control__error", ref: expandableElementRef, "aria-labelledby": labelId }, /* @__PURE__ */ React.createElement(Warning, { "aria-hidden": true, className: "segmented-control__error-icon", size: IconSize.Small }), /* @__PURE__ */ React.createElement(Text, { className: "segmented-control__error-message", size: "s", "aria-live": "polite" }, error))
    )
  );
};
export const StricterOptionGroup = (props) => {
  return /* @__PURE__ */ React.createElement(SegmentedControl, __spreadValues({}, props));
};
const renderOptionContent = (option) => {
  if (option.icon) {
    return option.icon;
  } else {
    return option.label;
  }
};
export default SegmentedControl;
